import React, { useState } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { UploadPanel } from '../documents/UploadPanel';
import { AskQuestion } from './AskQuestion';
import { Button } from '../ui/Button';
import { ChoiceCard } from '../ui/ChoiceCard';
import { HelpPanel } from '../ui/HelpPanel';
import { TextField } from '../ui/TextField';
import { loanOfficer, processor } from '../../data/team';
import { useTokens } from '../../hooks/useTokens';
import { usePortal } from '../../contexts/PortalContext';
interface BodyProps {
  onComplete: () => void;
}
export function DocumentRequestBody({
  onComplete
}: BodyProps) {
  const {
    tokens
  } = useTokens();
  return <>
      <div className="rounded-3xl bg-white p-5 ring-1 ring-ink-200 md:p-6" data-id="element-593" data-mp="2olrs2g57llr1">
        <p className="mb-4 text-sm text-ink-500" data-id="element-594" data-mp="2onumrdhb4323">
          Include <span className="font-medium text-ink-900" data-id="element-595" data-mp="32mkkmvbya6ri">all pages</span> of your <span className="font-medium text-ink-900" data-id="element-596" data-mp="2yk7i0fsi2oyy">{tokens.primaryAccountName} checking statement ending 4471</span> for Sep 1 – Sep 30.
        </p>
        <UploadPanel sampleName="september-statement.pdf" onSubmit={onComplete} submitLabel={`Send to ${processor.firstName}`} data-id="element-597" data-mp="2ouszc4898lbp" />
      </div>
      <AskQuestion name={processor.firstName} data-id="element-598" data-mp="2xtwi173ieypo" />
    </>;
}
export function QuestionBody({
  onComplete
}: BodyProps) {
  const [source, setSource] = useState('');
  const [note, setNote] = useState('');
  const {
    tokens
  } = useTokens();
  const sources = ['Gift from family', 'Transfer from my own account', 'Sold something', 'Work bonus or refund', 'Something else'];
  return <>
      <div className="rounded-2xl bg-ink-50 p-4" data-id="element-599" data-mp="3brbc4p4pjunz">
        <p className="text-[13px] text-ink-500" data-id="element-600" data-mp="3glfmlid6mktt">{tokens.primaryAccountName} checking ••4471</p>
        <div className="mt-1 flex items-baseline justify-between" data-id="element-601" data-mp="3lifqpnqh1dia">
          <p className="font-medium text-ink-900" data-id="element-602" data-mp="3qiu13cfjozjh">Deposit · Aug 18</p>
          <p className="tabular font-semibold text-ink-900" data-id="element-603" data-mp="3qgh9s8nwdctr">+$6,000.00</p>
        </div>
      </div>
      <fieldset className="mt-6" data-id="element-604" data-mp="3vfkfzwfm5p3b">
        <legend className="mb-3 text-sm font-medium text-ink-700" data-id="element-605" data-mp="35sztul81zllt">Where did this money come from?</legend>
        <div role="radiogroup" className="grid gap-2" data-id="element-606" data-mp="30uy9lwyw5g8r">
          {sources.map(s => <ChoiceCard key={s} title={s} selected={source === s} onSelect={() => setSource(s)} data-id="element-607" data-mp="3fj2n8mj371ko" />)}
        </div>
      </fieldset>
      <div className="mt-6" data-id="element-608" data-mp="3arudfpduhe6z">
        <label htmlFor="note" className="mb-1.5 block text-sm font-medium text-ink-700" data-id="element-609" data-mp="3aqjafrs84agx">
          A short explanation <span className="font-normal text-ink-400" data-id="element-610" data-mp="3jkxwcd79ej90">— one sentence is plenty</span>
        </label>
        <textarea id="note" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. I moved money from my Ally savings account." className="w-full resize-none rounded-xl bg-white p-3.5 text-[15px] ring-1 ring-inset ring-ink-200 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-pulse-600" data-id="element-611" data-mp="2csq6epn1od1" />
      </div>
      <div className="mt-4" data-id="element-612" data-mp="1z64aufig1qw1">
        <HelpPanel question="Why do large deposits matter?" data-id="element-613" data-mp="1z6xcj22jpq7b">Lenders need to confirm your down payment isn’t borrowed. A quick note usually clears it up — no need to worry.</HelpPanel>
      </div>
      <Button size="lg" className="mt-8 w-full sm:w-auto" disabled={!source || !note.trim()} onClick={onComplete} data-id="element-614" data-mp="jlbrnnszn5q7">
        Send answer
      </Button>
    </>;
}
const slots = [{
  day: 'Thu, Oct 8',
  times: ['9:00 AM', '11:30 AM', '2:00 PM']
}, {
  day: 'Fri, Oct 9',
  times: ['10:00 AM', '1:00 PM', '3:30 PM']
}, {
  day: 'Mon, Oct 12',
  times: ['9:30 AM', '12:00 PM']
}];
export function AppraisalBody({
  onComplete
}: BodyProps) {
  const [slot, setSlot] = useState('');
  const [contact, setContact] = useState<'me' | 'agent' | ''>('agent');
  return <>
      <div className="flex items-center gap-3 rounded-2xl bg-positive-50 px-4 py-3 text-sm text-positive-700" data-id="element-615" data-mp="3n3wp7zuck94y">
        <CheckCircle2Icon className="h-4 w-4 shrink-0" aria-hidden data-id="element-616" data-mp="1gdp48glsqqsh" /> Your appraisal has been ordered and paid for. No vendor to choose.
      </div>
      <h2 className="mb-3 mt-8 text-sm font-medium text-ink-700" data-id="element-617" data-mp="3hemycfb03iy1">Pick a time the appraiser can visit</h2>
      <div className="space-y-5" data-id="element-618" data-mp="3cfs6yoyywm2b">
        {slots.map(d => <div key={d.day} data-id="element-619" data-mp="3cgbnq2cvt4er">
            <p className="mb-2 text-[13px] text-ink-500" data-id="element-620" data-mp="38dx6fmov2cpl">{d.day}</p>
            <div role="radiogroup" aria-label={d.day} className="grid grid-cols-3 gap-2" data-id="element-621" data-mp="38c40k9m2nwin">
              {d.times.map(t => {
            const id = `${d.day} ${t}`;
            const on = slot === id;
            return <button key={t} role="radio" aria-checked={on} onClick={() => setSlot(id)} className={`h-11 rounded-xl text-sm font-medium ring-1 ring-inset transition-[background-color,color,box-shadow] duration-150 ${on ? 'bg-pulse-600 text-white ring-pulse-600' : 'bg-white text-ink-700 ring-ink-200 hover:ring-ink-300'}`} data-id="element-622" data-mp="2tpasnpevtt0h">
                    {t}
                  </button>;
          })}
            </div>
          </div>)}
      </div>
      <h2 className="mb-3 mt-8 text-sm font-medium text-ink-700" data-id="element-623" data-mp="2as9m405ojkuf">Who will let the appraiser in?</h2>
      <div role="radiogroup" className="grid gap-2 sm:grid-cols-2" data-id="element-624" data-mp="qa8hmyzs4fj4">
        <ChoiceCard title="My real estate agent" description="We’ll coordinate with them." selected={contact === 'agent'} onSelect={() => setContact('agent')} data-id="element-625" data-mp="v5xkpcb0cek6" />
        <ChoiceCard title="I will" description="We’ll text you a reminder." selected={contact === 'me'} onSelect={() => setContact('me')} data-id="element-626" data-mp="147nc4i4w3dhy" />
      </div>
      <Button size="lg" className="mt-8 w-full sm:w-auto" disabled={!slot || !contact} onClick={onComplete} data-id="element-627" data-mp="7j7jgd6hgo0m">
        Confirm {slot ? slot.replace(/^\w+, /, '') : 'time'}
      </Button>
    </>;
}
export function TitleBody({
  onComplete
}: BodyProps) {
  const {
    answers
  } = usePortal();
  const married = answers.maritalStatus === 'married';
  const [vesting, setVesting] = useState(married ? 'joint' : 'sole');
  const [coName, setCoName] = useState('');
  return <>
      <div role="radiogroup" className="grid gap-3" data-id="element-628" data-mp="3ox30z7kjss4y">
        <ChoiceCard title="Just me" description="Only your name is on the deed." selected={vesting === 'sole'} onSelect={() => setVesting('sole')} data-id="element-629" data-mp="w3nzjaq98w7x" />
        <ChoiceCard title={married ? 'Me and my spouse, together' : 'Me and someone else, together'} description="If one owner passes away, the other automatically owns the home." selected={vesting === 'joint'} onSelect={() => setVesting('joint')} data-id="element-630" data-mp="r3rt74u34sp7" />
        <ChoiceCard title="Me and someone else, in shares" description="Each person owns a share that can be passed to their heirs." selected={vesting === 'common'} onSelect={() => setVesting('common')} data-id="element-631" data-mp="d5l3wd37c115" />
      </div>
      {vesting !== 'sole' && <div className="mt-6" data-id="element-632" data-mp="i3wdqnthybjd">
          <TextField label="Full legal name of the other owner" value={coName} onChange={setCoName} data-id="element-633" data-mp="i3mwppqfjept" />
        </div>}
      <div className="mt-6" data-id="element-634" data-mp="1xjbwxyf1qiw3">
        <HelpPanel question="Not sure which to choose?" data-id="element-635" data-mp="1xmpzb6npms3j">
          This is a legal decision, and the options available depend on the laws in {answers.propertyState || 'your state'}. If you’re unsure, {loanOfficer.firstName} can connect you with the title company, or you may want to consult an attorney.
        </HelpPanel>
      </div>
      <Button size="lg" className="mt-8 w-full sm:w-auto" disabled={vesting !== 'sole' && !coName.trim()} onClick={onComplete} data-id="element-636" data-mp="1r4wlukxedcgm">
        Save my choice
      </Button>
    </>;
}